import { useEffect, useMemo } from 'react';
import { useError } from 'react-use';

import { useRestPostQuery } from 'utils/hooks/api/useRestPostQuery';
import {
  InsightQueryRequest,
  BehaviorQuery,
  BehaviorQueryKeysMap,
  RawBehaviorQuery,
  DEFAULT_QUERY,
} from '../../domain';
import { retryOnPartialContent } from '../utils';
import { useInsightsResponseHandler } from './useInsightsResponseHandler';

const BEHAVIORAL_INSIGHTS_API = '/lists/insights/behavioral';

export const isCustomBehaviorMetric = (event: string) =>
  [
    'Total Custom Conversion',
    'Custom Conversion Rate',
    'Conversion Rate',
    'Total Conversions',
  ].some((conversionType) => event.startsWith(conversionType));

const getCustomBehaviorMetric = (key: string) => {
  const exp = /^.* \((.+)\)$/;
  const match = key.match(exp);
  return match ? match[1] : '';
};

export const useBehavioralInsightsQuery = ({
  searchQuery,
  emailListId,
  endDate,
  startDate,
}: InsightQueryRequest = DEFAULT_QUERY) => {
  const params = useMemo(
    () => ({
      searchQuery,
      emailListId,
      endDate,
      startDate,
    }),
    [searchQuery, emailListId, endDate, startDate],
  );

  const { handleResponse, data = {}, isRetrying } = useInsightsResponseHandler<RawBehaviorQuery>();

  const { refetch, isLoading, ...results } = useRestPostQuery<
    RawBehaviorQuery,
    InsightQueryRequest
  >(
    BEHAVIORAL_INSIGHTS_API,
    params,
    {
      enabled: false,
      retry: retryOnPartialContent,
      onError: useError(),
    },
    undefined,
    handleResponse,
  );

  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params]);

  const modifiedBehavior: BehaviorQuery = (
    Object.keys(BehaviorQueryKeysMap) as Array<keyof RawBehaviorQuery>
  ).reduce(
    (aggregator: Partial<BehaviorQuery>, key) => ({
      ...aggregator,
      [BehaviorQueryKeysMap[key]]: data[key],
    }),
    {},
  );

  const customBehavior: BehaviorQuery = (Object.keys(data) as Array<keyof RawBehaviorQuery>).reduce(
    (aggregator: Partial<BehaviorQuery>, key) =>
      isCustomBehaviorMetric(key)
        ? { ...aggregator, [getCustomBehaviorMetric(key)]: data[key] }
        : aggregator,
    {},
  );

  return {
    ...results,
    refetch,
    isLoading: isLoading && !isRetrying,
    data: { ...modifiedBehavior, ...customBehavior },
  };
};
